import orientationAutoRaw from "../icons/print-orientation-auto.svg?raw";
import orientationLandscapeRaw from "../icons/print-orientation-landscape.svg?raw";
import orientationPortraitRaw from "../icons/print-orientation-portrait.svg?raw";
import regBullseyeRaw from "../icons/print-reg-bullseye.svg?raw";
import regCrosshairRaw from "../icons/print-reg-crosshair.svg?raw";
import regNoneRaw from "../icons/print-reg-none.svg?raw";
import regScissorsRaw from "../icons/print-reg-scissors.svg?raw";
import { markPaidFeature } from "./paidPreview";
import { stripIconSvg } from "../icons/stripIconSvg";
import type { EditorContext } from "../tools/EditorContext";
import {
  downloadPdf,
  isAboveTabloid,
  printPdf,
  type PageOrientation,
  type PageSizeId,
  type PdfOptions,
  type RegistrationMark,
} from "../exporters/pdfExporter";
import { getUnits } from "../preferences";
import { isFeatureEnabled } from "../featureFlags";
import { mmInchInputs } from "./mmInchInputs";
import {
  printPartnerNamesFor,
  printPromptFor,
  renderPrintServices,
} from "./printPartners";

/** Default page margin. Metric matches the basic flow's `PdfOptions.margin`
 *  (10 mm); imperial uses a round 0.5". */
const DEFAULT_MARGIN_MM = 10;
const IMPERIAL_DEFAULT_MARGIN_MM = 0.5 * 25.4; // 12.7 mm

interface PaperSize {
  value: PageSizeId;
  name: string;
  /** Alternate name used in landscape (the 11×17 sheet is Tabloid in portrait,
   *  Ledger in landscape). */
  landscapeName?: string;
  /** Secondary designation shown alongside the name (e.g. Letter is ANSI A). */
  alias?: string;
  /** Natural portrait dimensions. */
  w: number;
  h: number;
  unit: string;
}

/** Paper sizes offered in the dialog, in portrait orientation. ISO A series up
 *  to A0 (metric) and the US/ANSI series up to ANSI E (~A0, imperial). */
const PAPER_SIZES: PaperSize[] = [
  { value: "a5", name: "A5", w: 148, h: 210, unit: "mm" },
  { value: "a4", name: "A4", w: 210, h: 297, unit: "mm" },
  { value: "a3", name: "A3", w: 297, h: 420, unit: "mm" },
  { value: "a2", name: "A2", w: 420, h: 594, unit: "mm" },
  { value: "a1", name: "A1", w: 594, h: 841, unit: "mm" },
  { value: "a0", name: "A0", w: 841, h: 1189, unit: "mm" },
  // Letter and Tabloid are ANSI A and ANSI B, so the rest of the ANSI series
  // (C / D / E) follows them, then the architectural sizes.
  {
    value: "letter",
    name: "Letter",
    alias: "ANSI A",
    landscapeName: "Letter Wide",
    w: 8.5,
    h: 11,
    unit: "in",
  },
  {
    value: "legal",
    name: "Legal",
    alias: "ANSI A+",
    landscapeName: "Legal Wide",
    w: 8.5,
    h: 14,
    unit: "in",
  },
  {
    value: "tabloid",
    name: "Tabloid",
    alias: "ANSI B",
    landscapeName: "Ledger",
    w: 11,
    h: 17,
    unit: "in",
  },
  { value: "ansi-c", name: "ANSI C", w: 17, h: 22, unit: "in" },
  { value: "ansi-d", name: "ANSI D", w: 22, h: 34, unit: "in" },
  { value: "ansi-e", name: "ANSI E", w: 34, h: 44, unit: "in" },
  { value: "arch-c", name: "Arch C", w: 18, h: 24, unit: "in" },
  { value: "arch-d", name: "Arch D", w: 24, h: 36, unit: "in" },
  { value: "arch-e", name: "Arch E", w: 36, h: 48, unit: "in" },
  { value: "arch-e1", name: "Arch E1", w: 30, h: 42, unit: "in" },
];

/** Option label for a paper size, with the name and dimensions following the
 *  current orientation (landscape swaps width / height and may rename). Print
 *  partners are appended only when `withPartners` is set — the dropdown shows
 *  them while expanded but not on the collapsed (selected) value. */
function sizeOptionLabel(
  p: PaperSize,
  landscape: boolean,
  withPartners: boolean,
): string {
  const name = landscape && p.landscapeName ? p.landscapeName : p.name;
  const alias = p.alias ? ` / ${p.alias}` : "";
  const [a, b] = landscape ? [p.h, p.w] : [p.w, p.h];
  const partners = withPartners ? printPartnerNamesFor(p.value) : [];
  const suffix = partners.length ? `: ${partners.join(", ")}` : "";
  return `${name}${alias} (${a} × ${b} ${p.unit})${suffix}`;
}

/** Paper-size <option>s split into Metric and Imperial <optgroup>s (which the
 *  browser renders with a separating header). The active unit system's group
 *  comes first so the user's own units sit on top. */
function buildSizeOptions(imperialFirst: boolean): string {
  const metric = PAPER_SIZES.filter((p) => p.unit === "mm");
  const imperial = PAPER_SIZES.filter((p) => p.unit !== "mm");
  const groups: Array<[string, PaperSize[]]> = imperialFirst
    ? [
        ["Imperial", imperial],
        ["Metric", metric],
      ]
    : [
        ["Metric", metric],
        ["Imperial", imperial],
      ];
  return groups
    .map(
      ([label, list]) =>
        `<optgroup label="${label}">${list
          .map((p) => `<option value="${p.value}"></option>`)
          .join("")}</optgroup>`,
    )
    .join("");
}

/** Icons for the radio options, read from `src/icons/print-*.svg` through the
 *  shared {@link stripIconSvg} (#216) and painted with `currentColor` so they
 *  follow the theme. The registration-mark glyphs mirror what the exporter
 *  actually draws. Their rendered size is `.print-radio-icon svg` in
 *  styles.css — the strip removes any the files carried. */
const ICONS: Record<string, string> = {
  // Orientation
  auto: stripIconSvg(orientationAutoRaw),
  portrait: stripIconSvg(orientationPortraitRaw),
  landscape: stripIconSvg(orientationLandscapeRaw),
  // Registration marks
  none: stripIconSvg(regNoneRaw),
  crosshair: stripIconSvg(regCrosshairRaw),
  bullseye: stripIconSvg(regBullseyeRaw),
  scissors: stripIconSvg(regScissorsRaw),
};

interface RadioOption {
  value: string;
  label: string;
  icon: string;
}

const ORIENTATION_OPTIONS: RadioOption[] = [
  { value: "auto", label: "Auto", icon: ICONS.auto! },
  { value: "portrait", label: "Portrait", icon: ICONS.portrait! },
  { value: "landscape", label: "Landscape", icon: ICONS.landscape! },
];

const REGISTRATION_OPTIONS: RadioOption[] = [
  { value: "scissors", label: "Scissors", icon: ICONS.scissors! },
  { value: "crosshair", label: "Cross-hairs", icon: ICONS.crosshair! },
  { value: "bullseye", label: "Bullseye", icon: ICONS.bullseye! },
  { value: "none", label: "None", icon: ICONS.none! },
];

/** Render one icon-labelled radio group. The first option is selected by
 *  default unless one is marked otherwise via `selected`. */
function radioGroup(
  legend: string,
  name: string,
  options: RadioOption[],
  selected: string,
): string {
  const items = options
    .map(
      (o) => `
      <label class="radio-card" style="display:flex;align-items:center;gap:5px;padding:4px 7px;border:1px solid rgba(127,127,127,0.4);border-radius:6px;cursor:pointer;white-space:nowrap;">
        <input type="radio" name="${name}" value="${o.value}" ${o.value === selected ? "checked" : ""} />
        <span class="print-radio-icon">${o.icon}</span>
        <span>${o.label}</span>
      </label>`,
    )
    .join("");
  // No wrap — the dialog is sized to hold each group on a single line.
  return `
    <div style="display:grid;gap:4px;">
      <span>${legend}</span>
      <div style="display:flex;flex-wrap:nowrap;gap:6px;">${items}</div>
    </div>`;
}

/** Mode the settings dialog runs in: send the built PDF to the print dialog,
 *  or download it as a file. */
type PrintMode = "print" | "export";

/** Panel (hidden until a size with print partners is chosen) listing third-party
 *  print services for the selected paper size — see `printPartners.ts`. Lives in
 *  the footer, left of the action buttons. */
const PRINT_SERVICES_HTML = `
  <div data-field="printServices" style="display:none;flex-direction:column;align-items:flex-start;gap:4px;font-size:12px;">
    <span data-field="servicesPrompt" style="opacity:0.8;"></span>
    <span data-field="serviceButtons" style="display:inline-flex;flex-wrap:wrap;gap:8px;align-items:center;"></span>
  </div>`;

/** Advanced printing / export settings dialog (issue #289). Shown — instead of
 *  printing / exporting straight away — when the `advanced-printing` feature flag
 *  is on, so the user can pick orientation, paper size, margin, crop marks, and
 *  registration marks before the PDF is built. The same dialog drives both Print
 *  (hands the PDF to the browser print dialog) and PDF Export (downloads it).
 *
 *  Unlike the basic flow, the chosen page size is used exactly (`fitPaper:
 *  "exact"`) rather than fitting the A4/Letter common area — the user has
 *  explicitly committed to a paper here. */
export class AdvancedPrintDialog {
  private host: HTMLElement;
  private ctx: EditorContext;
  /** Current margin in mm, kept in sync by the dual-unit input. */
  private marginMm = DEFAULT_MARGIN_MM;
  private dialogEl: HTMLDialogElement | null = null;

  constructor(host: HTMLElement, ctx: EditorContext) {
    this.host = host;
    this.ctx = ctx;
  }

  open(mode: PrintMode = "print") {
    this.teardown();

    const defaultSize: PageSizeId = getUnits() === "in" ? "letter" : "a4";
    const title = mode === "export" ? "PDF export settings" : "Print settings";

    // A native <dialog> opened with showModal() puts the rest of the page in
    // the inert top-layer background, so focus stays trapped inside the dialog.
    const modal = document.createElement("dialog");
    modal.className = "modal advanced-print-modal";
    modal.innerHTML = `
      <header style="display:flex;align-items:center;justify-content:space-between;gap:12px;">
        <span>${title}</span>
        <button class="btn" type="button" data-act="close" aria-label="Close" title="Close" style="border:none;background:transparent;font-size:18px;line-height:1;padding:2px 8px;cursor:pointer;color:inherit;">✕</button>
      </header>
      <form data-field="printForm" style="margin:0;">
      <div class="body">
        <div style="display:grid;gap:12px;min-width:470px;">
          ${radioGroup("Orientation", "orientation", ORIENTATION_OPTIONS, "auto")}
          <div style="display:flex;gap:12px;align-items:end;">
            <label style="display:grid;gap:4px;flex:7;min-width:0;">
              <span>Paper size</span>
              <select data-field="pageSize">
                ${buildSizeOptions(getUnits() === "in")}
              </select>
            </label>
            <label style="display:grid;gap:4px;flex:3;min-width:0;">
              <span>Margin</span>
              <div data-field="marginSlot"></div>
            </label>
          </div>
          ${radioGroup("Registration marks", "registrationMark", REGISTRATION_OPTIONS, "scissors")}
          <label style="display:flex;gap:8px;align-items:center;">
            <input type="checkbox" data-field="cropMarks" checked />
            <span>Crop marks (corner trim guides)</span>
          </label>
          <fieldset class="print-content-fields" style="border:1px solid rgba(127,127,127,0.4);border-radius:6px;padding:8px 10px;margin:0;">
            <legend style="padding:0 4px;">Content</legend>
            <div class="print-content-group">
              <label style="display:flex;gap:8px;align-items:center;">
                <input type="checkbox" data-field="includeCoverPage" checked />
                <span data-field="coverLabel">Title &amp; scale-check page</span>
              </label>
            </div>
            <div class="print-content-group">
              <label style="display:flex;gap:8px;align-items:center;">
                <input type="checkbox" data-field="includePattern" checked />
                <span data-field="patternLabel">Pattern</span>
              </label>
              <label style="display:flex;gap:8px;align-items:center;margin-left:22px;">
                <input type="checkbox" data-field="includeConstructionLines" checked />
                <span>Construction lines</span>
              </label>
              <label style="display:flex;gap:8px;align-items:center;margin-left:22px;">
                <input type="checkbox" data-field="includePointMarkers" checked />
                <span>${
                  // Name only what the sheet will actually carry: with
                  // `point-letters` off no letter is ever drawn, so promising
                  // them in the label is a control that lies about its own
                  // effect.
                  isFeatureEnabled("point-letters")
                    ? "Point markers and letters"
                    : "Point markers"
                }</span>
              </label>
            </div>
            <div class="print-content-group">
              <label style="display:flex;gap:8px;align-items:center;">
                <input type="checkbox" data-field="includeNotes" checked />
                <span>Notes</span>
              </label>
              <label style="display:flex;gap:8px;align-items:center;margin-left:22px;">
                <input type="checkbox" data-field="includeNotesOnPattern" checked />
                <span>On the pattern</span>
              </label>
              <label style="display:flex;gap:8px;align-items:center;margin-left:22px;">
                <input type="checkbox" data-field="includeNotesPage" checked />
                <span>Notes page</span>
              </label>
            </div>
            <div class="print-content-group">
              <label style="display:flex;gap:8px;align-items:center;">
                <input type="checkbox" data-field="includeInstructions" checked />
                <span data-field="instructionsLabel">Step-by-step instructions</span>
              </label>
            </div>
          </fieldset>
        </div>
      </div>
      <footer style="display:flex;align-items:stretch;gap:0;padding:0;">
        <div style="flex:1;min-width:0;display:flex;align-items:flex-end;flex-wrap:wrap;gap:8px;padding:12px;border-right:1px solid var(--border);">
          ${PRINT_SERVICES_HTML}
          <button class="btn" type="button" data-act="download">Download PDF</button>
        </div>
        <div style="display:flex;align-items:center;gap:8px;padding:12px 16px;">
          <button class="btn" type="button" data-act="close">Close</button>
          <button class="btn primary" type="submit" data-act="print">Print</button>
        </div>
      </footer>
      </form>
    `;
    // The whole dialog is advanced printing, so its title wears the Preview
    // badge (#1120) rather than any one option in it.
    const printTitle = modal.querySelector<HTMLElement>("header > span");
    if (printTitle) markPaidFeature(printTitle, "advanced-printing");

    // Default the paper select to the active unit system.
    const sizeSel = modal.querySelector(
      '[data-field="pageSize"]',
    ) as HTMLSelectElement;
    sizeSel.value = defaultSize;

    // Relabel each paper option to follow the orientation: landscape swaps the
    // dimensions and renames the 11×17 sheet (Tabloid → Ledger). Print partners
    // are appended only while the dropdown is expanded — a native <select> shows
    // the selected option's text when collapsed, where the partner list would be
    // noise, so we strip it there and restore it on open.
    let sizeExpanded = false;
    const refreshSizeLabels = () => {
      const landscape =
        (
          modal.querySelector(
            'input[name="orientation"]:checked',
          ) as HTMLInputElement | null
        )?.value === "landscape";
      for (const p of PAPER_SIZES) {
        const opt = sizeSel.querySelector(
          `option[value="${p.value}"]`,
        ) as HTMLOptionElement | null;
        if (opt) opt.textContent = sizeOptionLabel(p, landscape, sizeExpanded);
      }
    };
    const setSizeExpanded = (v: boolean) => {
      if (sizeExpanded === v) return;
      sizeExpanded = v;
      refreshSizeLabels();
    };
    // Expand on mouse open or a key that opens the dropdown; collapse on a pick
    // or when focus leaves.
    sizeSel.addEventListener("mousedown", () => setSizeExpanded(true));
    sizeSel.addEventListener("keydown", (e) => {
      if ([" ", "Enter", "ArrowDown", "ArrowUp"].includes(e.key)) {
        setSizeExpanded(true);
      }
    });
    sizeSel.addEventListener("change", () => setSizeExpanded(false));
    sizeSel.addEventListener("blur", () => setSizeExpanded(false));
    modal
      .querySelectorAll('input[name="orientation"]')
      .forEach((el) => el.addEventListener("change", refreshSizeLabels));
    refreshSizeLabels();

    // The title / scale-check cover page and the instructions both default off
    // once the paper is larger than Tabloid (large-format / blueprint printing
    // usually wants just the pattern) — the same sizing check for both. Each
    // follows the chosen size until the user touches its checkbox.
    const coverCb = modal.querySelector(
      '[data-field="includeCoverPage"]',
    ) as HTMLInputElement;
    let coverTouched = false;
    coverCb.addEventListener("change", () => {
      coverTouched = true;
    });
    const syncCoverDefault = () => {
      if (coverTouched) return;
      coverCb.checked = !isAboveTabloid(sizeSel.value as PageSizeId);
    };

    // The scale-check squares have a single home, chosen by priority (#577): the
    // cover page when it's printed; otherwise the instructions page when it's
    // printed; otherwise the pattern itself. The labels track where they land —
    // "& scale-check" appears on exactly one of the three (and on none when the
    // only thing left to host it, the pattern, is also excluded).
    const patternCb = modal.querySelector(
      '[data-field="includePattern"]',
    ) as HTMLInputElement;
    const instrCb = modal.querySelector(
      '[data-field="includeInstructions"]',
    ) as HTMLInputElement;
    const coverLabel = modal.querySelector(
      '[data-field="coverLabel"]',
    ) as HTMLElement;
    const patternLabel = modal.querySelector(
      '[data-field="patternLabel"]',
    ) as HTMLElement;
    const instructionsLabel = modal.querySelector(
      '[data-field="instructionsLabel"]',
    ) as HTMLElement;
    const syncContentLabels = () => {
      const coverHosts = coverCb.checked && patternCb.checked;
      const instructionsHost = !coverCb.checked && instrCb.checked;
      const patternHosts =
        !coverCb.checked && !instrCb.checked && patternCb.checked;
      coverLabel.textContent = coverHosts
        ? "Title & scale-check page"
        : "Title page";
      instructionsLabel.textContent = instructionsHost
        ? "Step-by-step instructions & scale-check"
        : "Step-by-step instructions";
      patternLabel.textContent = patternHosts
        ? "Pattern & scale-check"
        : "Pattern";
    };
    patternCb.addEventListener("change", syncContentLabels);
    coverCb.addEventListener("change", syncContentLabels);
    instrCb.addEventListener("change", syncContentLabels);
    syncContentLabels();

    // Construction lines and point markers only exist on the pattern pages —
    // grey their toggles out while the pattern itself is excluded (#870, #693).
    const constructionCb = modal.querySelector(
      '[data-field="includeConstructionLines"]',
    ) as HTMLInputElement;
    const pointMarkersCb = modal.querySelector(
      '[data-field="includePointMarkers"]',
    ) as HTMLInputElement;
    const syncConstructionToggle = () => {
      constructionCb.disabled = !patternCb.checked;
      pointMarkersCb.disabled = !patternCb.checked;
    };
    patternCb.addEventListener("change", syncConstructionToggle);
    syncConstructionToggle();

    // Notes are one decision with two places to put them (#157), so they read
    // as a parent and two children — the same shape as Pattern above. Turning
    // the parent off greys both out, and the collected options AND them, so an
    // unchecked parent means no notes whichever way its children are left.
    // "On the pattern" additionally needs the pattern itself: it draws on those
    // pages and nowhere else.
    const notesCb = modal.querySelector(
      '[data-field="includeNotes"]',
    ) as HTMLInputElement;
    const notesOnPatternCb = modal.querySelector(
      '[data-field="includeNotesOnPattern"]',
    ) as HTMLInputElement;
    const notesPageCb = modal.querySelector(
      '[data-field="includeNotesPage"]',
    ) as HTMLInputElement;
    const syncNotesToggles = () => {
      notesOnPatternCb.disabled = !notesCb.checked || !patternCb.checked;
      notesPageCb.disabled = !notesCb.checked;
    };
    notesCb.addEventListener("change", syncNotesToggles);
    patternCb.addEventListener("change", syncNotesToggles);
    syncNotesToggles();

    // Instructions use the same above-Tabloid check as the cover above.
    let instrTouched = false;
    instrCb.addEventListener("change", () => {
      instrTouched = true;
    });
    const syncInstructionsDefault = () => {
      if (instrTouched) return;
      instrCb.checked = !isAboveTabloid(sizeSel.value as PageSizeId);
    };

    // Surface third-party print services that cover the selected paper size
    // (config in printPartners.ts); hide the panel when none match.
    const servicesPanel = modal.querySelector(
      '[data-field="printServices"]',
    ) as HTMLElement;
    const serviceButtons = modal.querySelector(
      '[data-field="serviceButtons"]',
    ) as HTMLElement;
    const servicesPrompt = modal.querySelector(
      '[data-field="servicesPrompt"]',
    ) as HTMLElement;
    const syncPrintServices = () => {
      const size = sizeSel.value as PageSizeId;
      const html = renderPrintServices(size);
      serviceButtons.innerHTML = html;
      servicesPrompt.textContent = printPromptFor(size);
      servicesPanel.style.display = html ? "flex" : "none";
    };

    sizeSel.addEventListener("change", () => {
      syncCoverDefault();
      syncInstructionsDefault();
      // The cover may have auto-toggled, moving where the scale-check lives.
      syncContentLabels();
      syncPrintServices();
    });
    syncCoverDefault();
    syncInstructionsDefault();
    syncContentLabels();
    syncPrintServices();

    // Margin: the app's standard dual-unit (mm / in) length input, which
    // already orders the project's preferred unit first. Default follows the
    // unit system — 10 mm metric, 0.5" imperial. Tracks `marginMm`.
    const defaultMargin =
      getUnits() === "in" ? IMPERIAL_DEFAULT_MARGIN_MM : DEFAULT_MARGIN_MM;
    this.marginMm = defaultMargin;
    const { row: marginRow } = mmInchInputs(
      defaultMargin,
      (mm) => {
        this.marginMm = mm;
      },
      { minMm: 0, maxMm: 40 },
    );
    modal.querySelector('[data-field="marginSlot"]')!.appendChild(marginRow);

    const runPrint = async () => {
      const options = this.collect(modal);
      this.close();
      try {
        await printPdf(this.ctx.history.state, this.ctx.library, options);
      } catch (e) {
        alert("Print failed: " + (e as Error).message);
      }
    };

    modal.addEventListener("click", async (ev) => {
      // A click landing on the dialog element itself is a click on the
      // ::backdrop (outside the content) — dismiss, like the old overlay.
      if (ev.target === modal) {
        this.close();
        return;
      }
      const btn = (ev.target as HTMLElement).closest(
        "[data-act]",
      ) as HTMLButtonElement | null;
      if (!btn) return;
      const act = btn.dataset.act;
      if (act === "close") {
        this.close();
        return;
      }
      if (act === "download") {
        // Keep the dialog open so the user can download again or then print.
        const options = this.collect(modal);
        try {
          await downloadPdf(this.ctx.history.state, this.ctx.library, options);
        } catch (e) {
          alert("Export failed: " + (e as Error).message);
        }
      }
    });

    // Print is the form's submit (primary) button, so Enter from any field
    // triggers it. Swallow the native submit so the page doesn't navigate.
    const form = modal.querySelector(
      '[data-field="printForm"]',
    ) as HTMLFormElement;
    form.addEventListener("submit", (ev) => {
      ev.preventDefault();
      void runPrint();
    });

    this.host.appendChild(modal);
    this.dialogEl = modal;
    // Native Esc / programmatic close fires "close"; tear down then.
    modal.addEventListener("close", () => this.teardown());
    modal.showModal();
    // showModal() focuses the first focusable element — the header ✕ button —
    // which would make Enter close the dialog. Focus the primary Print (submit)
    // button instead so Enter submits the form / triggers print by default.
    (modal.querySelector('[data-act="print"]') as HTMLButtonElement).focus();
  }

  /** Read the form into PdfOptions. */
  private collect(modal: HTMLElement): PdfOptions {
    const val = (sel: string) =>
      (modal.querySelector(`[data-field="${sel}"]`) as HTMLInputElement).value;
    const checked = (sel: string) =>
      (modal.querySelector(`[data-field="${sel}"]`) as HTMLInputElement)
        .checked;
    const radio = (name: string) =>
      (modal.querySelector(`input[name="${name}"]:checked`) as HTMLInputElement)
        .value;
    return {
      orientation: radio("orientation") as PageOrientation,
      pageSize: val("pageSize") as PageSizeId,
      margin: this.marginMm,
      registrationMark: radio("registrationMark") as RegistrationMark,
      cropMarks: checked("cropMarks"),
      // Advanced printing (this dialog only opens when its flag is on) drops the
      // watermark.
      watermark: false,
      includePattern: checked("includePattern"),
      includeConstructionLines: checked("includeConstructionLines"),
      includePointMarkers: checked("includePointMarkers"),
      includeCoverPage: checked("includeCoverPage"),
      includeInstructions: checked("includeInstructions"),
      includeNotesPage: checked("includeNotes") && checked("includeNotesPage"),
      includeNotesOnPattern:
        checked("includeNotes") && checked("includeNotesOnPattern"),
      fitPaper: "exact",
    };
  }

  close() {
    // close() fires the "close" event, where teardown() removes the element.
    this.dialogEl?.close();
  }

  /** Remove the dialog element and reset state. Idempotent. */
  private teardown() {
    if (this.dialogEl) {
      this.dialogEl.remove();
      this.dialogEl = null;
    }
  }
}
